const SUBSCRIPTION_PROMO_CONFIG = {
  launchBasicFallbackEnabled: true,
  launchBasicSelectableWithoutBilling: true,
  launchBasicPriceOverride: {
    cadPerUserMonthly: 0,
    usdPerUserMonthly: 0,
    eurPerUserMonthly: 0,
    gbpPerUserMonthly: 0,
  },
  launchBasicIncludesTrial: false,
};

const STORAGE_ADD_ON_OPTIONS = [
  {
    key: "storage_250",
    totalStorageGb: 250,
    pricing: {
      cadMonthly: 1.5,
      usdMonthly: 1.5,
      eurMonthly: 1,
      gbpMonthly: 1,
    },
  },
  {
    key: "storage_300",
    totalStorageGb: 300,
    pricing: {
      cadMonthly: 3,
      usdMonthly: 2.5,
      eurMonthly: 2,
      gbpMonthly: 2,
    },
  },
  {
    key: "storage_450",
    totalStorageGb: 450,
    pricing: {
      cadMonthly: 8,
      usdMonthly: 5.2,
      eurMonthly: 4.5,
      gbpMonthly: 4,
    },
  },
  {
    key: "storage_700",
    totalStorageGb: 700,
    pricing: {
      cadMonthly: 15,
      usdMonthly: 10.5,
      eurMonthly: 9,
      gbpMonthly: 8,
    },
  },
  {
    key: "storage_1000",
    totalStorageGb: 1000,
    pricing: {
      cadMonthly: 24,
      usdMonthly: 16.5,
      eurMonthly: 14,
      gbpMonthly: 12.5,
    },
  },
];

const TEAM_SIZE_INTRO_DISCOUNT_CONFIG = {
  eligiblePlanKeys: ["premier", "collegiate", "pro"],
  durationMonths: 3,
  tiers: [
    { minUsers: 7, percentOff: 10 },
    { minUsers: 6, percentOff: 6 },
    { minUsers: 5, percentOff: 3 },
  ],
};

const FULL_TEAM_MODE_DEFAULTS = {
  thresholdMembers: 5,
  priceCapUsers: 5,
  roleCaps: {
    player: 5,
    coach: 1,
    analyst: 1,
    manager: 1,
    sub: 2,
    trialing: 3,
  },
};

const SUBSCRIPTION_PLANS = {
  free: {
    label: "Free",
    storageGb: 0,
    monthlyUploadGb: 0,
    maxMembers: 7,
    billingIntervals: [],
    requiresBillingSetup: false,
    launchPromoSelectableWithoutBilling: false,
    supportsFullTeamMode: false,
    fullTeamModeThresholdMembers: null,
    fullTeamModePriceCapUsers: null,
    fullTeamModeRoleCaps: null,
    features: {
      vodReviewer: false,
      teamStats: false,
      teamCalendar: false,
      prepCenter: false,
      personalCalendar: false,
      personalAvailability: false,
      personalHighlights: false,
      personalFavorites: false,
      notes: false,
      lfg: true,
    },
  },

  basic: {
    label: "Basic",
    storageGb: 0,
    monthlyUploadGb: 0,
    hasHardUploadCap: true,
    maxMembers: 10,
    billingIntervals: ["monthly"],
    requiresBillingSetup: true,
    launchPromoSelectableWithoutBilling: true,
    supportsFullTeamMode: false,
    fullTeamModeThresholdMembers: null,
    fullTeamModePriceCapUsers: null,
    fullTeamModeRoleCaps: null,
    pricing: {
      cadPerUserMonthly: 3.99,
      usdPerUserMonthly: 2.99,
      eurPerUserMonthly: 2.69,
      gbpPerUserMonthly: 2.29,
    },
    features: {
      vodReviewer: false,
      teamStats: true,
      teamCalendar: true,
      prepCenter: true,
      personalCalendar: true,
      personalAvailability: true,
      personalHighlights: true,
      personalFavorites: true,
      notes: true,
      lfg: true,
    },
  },

  premier: {
    label: "Premier",
    storageGb: 200,
    monthlyUploadGb: 36,
    hasHardUploadCap: true,
    maxMembers: Object.values(FULL_TEAM_MODE_DEFAULTS.roleCaps).reduce((sum, cap) => sum + cap, 0),
    billingIntervals: ["monthly"],
    requiresBillingSetup: true,
    launchPromoSelectableWithoutBilling: false,
    supportsFullTeamMode: true,
    fullTeamModeThresholdMembers: FULL_TEAM_MODE_DEFAULTS.thresholdMembers,
    fullTeamModePriceCapUsers: FULL_TEAM_MODE_DEFAULTS.priceCapUsers,
    fullTeamModeRoleCaps: FULL_TEAM_MODE_DEFAULTS.roleCaps,
    pricing: {
      cadPerUserMonthly: 14.99,
      usdPerUserMonthly: 10.99,
      eurPerUserMonthly: 9.99,
      gbpPerUserMonthly: 8.69,
    },
    features: {
      vodReviewer: true,
      teamStats: true,
      teamCalendar: true,
      prepCenter: true,
      personalCalendar: true,
      personalAvailability: true,
      personalHighlights: true,
      personalFavorites: true,
      notes: true,
      lfg: true,
    },
  },

  collegiate: {
    label: "Collegiate",
    storageGb: 200,
    monthlyUploadGb: 60,
    hasHardUploadCap: true,
    maxMembers: Object.values(FULL_TEAM_MODE_DEFAULTS.roleCaps).reduce((sum, cap) => sum + cap, 0),
    billingIntervals: ["monthly"],
    requiresBillingSetup: true,
    launchPromoSelectableWithoutBilling: false,
    supportsFullTeamMode: true,
    fullTeamModeThresholdMembers: FULL_TEAM_MODE_DEFAULTS.thresholdMembers,
    fullTeamModePriceCapUsers: FULL_TEAM_MODE_DEFAULTS.priceCapUsers,
    fullTeamModeRoleCaps: FULL_TEAM_MODE_DEFAULTS.roleCaps,
    pricing: {
      cadPerUserMonthly: 21.99,
      usdPerUserMonthly: 15.99,
      eurPerUserMonthly: 13.99,
      gbpPerUserMonthly: 12.49,
    },
    features: {
      vodReviewer: true,
      teamStats: true,
      teamCalendar: true,
      prepCenter: true,
      personalCalendar: true,
      personalAvailability: true,
      personalHighlights: true,
      personalFavorites: true,
      notes: true,
      lfg: true,
    },
  },

  pro: {
    label: "Pro",
    storageGb: 200,
    monthlyUploadGb: 200,
    hasHardUploadCap: false,
    maxMembers: Object.values(FULL_TEAM_MODE_DEFAULTS.roleCaps).reduce((sum, cap) => sum + cap, 0),
    billingIntervals: ["monthly"],
    requiresBillingSetup: true,
    launchPromoSelectableWithoutBilling: false,
    supportsFullTeamMode: true,
    fullTeamModeThresholdMembers: FULL_TEAM_MODE_DEFAULTS.thresholdMembers,
    fullTeamModePriceCapUsers: FULL_TEAM_MODE_DEFAULTS.priceCapUsers,
    fullTeamModeRoleCaps: FULL_TEAM_MODE_DEFAULTS.roleCaps,
    pricing: {
      cadPerUserMonthly: 59.99,
      usdPerUserMonthly: 49.99,
      eurPerUserMonthly: 38.49,
      gbpPerUserMonthly: 33.49,
    },
    uploadOveragePricing: {
      cadPerGb: 1.5,
      usdPerGb: 1.2,
      eurPerGb: 1,
      gbpPerGb: 1,
      chargedOnNextBillingCycle: true,
    },
    features: {
      vodReviewer: true,
      teamStats: true,
      teamCalendar: true,
      prepCenter: true,
      personalCalendar: true,
      personalAvailability: true,
      personalHighlights: true,
      personalFavorites: true,
      notes: true,
      lfg: true,
    },
  },
};

export {
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_PROMO_CONFIG,
  STORAGE_ADD_ON_OPTIONS,
  TEAM_SIZE_INTRO_DISCOUNT_CONFIG,
};
